// src/components/admin/TeamMembersManagement.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

function ModalContent({
  teamMemberToDelete,
  setShowDeleteModal,
  handleDeleteTeamMember,
  teamMembersLoading,
}) {
  if (teamMembersLoading) {
    return (
      <Modal.Body className="bg-dark text-white">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Modal.Body>
    );
  }
  return (
    <>
      <Modal.Header className="bg-dark text-white border-secondary">
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        Are you sure you want to delete the team member:
        <strong> {teamMemberToDelete?.name}</strong>?
        <br />
        This action cannot be undone.
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white border-secondary">
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteTeamMember}>
          Delete Team Member
        </Button>
      </Modal.Footer>
    </>
  );
}

export default function AdminTeamMembers() {
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamMemberToDelete, setTeamMemberToDelete] = useState(null);
  const { teamMembers, removeTeamMember, teamMembersLoading } = useData();
  const navigate = useNavigate();
  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);

  const handleEditTeamMember = (id) => {
    navigate(`/admin/team/edit/${id}`);
  };

  const confirmDelete = (teamMember) => {
    setTeamMemberToDelete(teamMember);
    setShowDeleteModal(true);
  };

  const handleDeleteTeamMember = async () => {
    try {
      await removeTeamMember(teamMemberToDelete.id);
    } catch (error) {
      setError(error.message);
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">Manage Team Members</h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back to Dashboard
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card
            className="bg-dark text-white border-secondary"
            style={{ width: "100%" }}
          >
            <Card.Body>
              <div className="table-responsive">
                <Table variant="dark" bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Order</th> {/* New column */}
                      <th>Name</th>
                      <th className="d-none d-md-table-cell">Position</th>
                      <th className="d-none d-md-table-cell">Description</th>
                      <th style={{ width: "140px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((teamMember, index) => (
                      <tr key={teamMember.id}>
                        <td>{teamMember.id}</td>
                        <td>{teamMember.display_order || 999}</td>{" "}
                        {/* New cell */}
                        <td>{teamMember.name}</td>
                        <td className="d-none d-md-table-cell">
                          {teamMember.position}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {teamMember.description.length > 100
                            ? `${teamMember.description.substring(0, 100)}...`
                            : teamMember.description}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                handleEditTeamMember(teamMember.id)
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmDelete(teamMember)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="bg-dark border-secondary">
              <Button
                variant="success"
                onClick={() => navigate("/admin/team/edit/new")}
              >
                Add New Team Member
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <ModalContent
          teamMemberToDelete={teamMemberToDelete}
          setShowDeleteModal={setShowDeleteModal}
          handleDeleteTeamMember={handleDeleteTeamMember}
          teamMembersLoading={teamMembersLoading}
        />
      </Modal>
    </Container>
  );
}
